import { GET_AUCTION_FORMS } from "core/apollo/graphql";
import { FIELD_CREATION_TYPE, SEARCH_ACTION_GENERATE_FORM } from "core/consts";
import { useApolloEncryptionContext } from "core/model/patients/encryption/utils";
import { getErrorMessage } from "core/model/utils/errors";
import {
  Auction,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
  DocsSubmittedFormData,
  FieldCreationType,
  FormMetadata,
  SourceFields,
} from "core/types";
import useSearchAction from "dsl/hooks/useSearchAction";
import { useCallback } from "react";
import { convertEncryptedFieldOut } from "react-forms-state";
import { SourceInformation } from "../../useDocsForm";
import { FORM_NAME_TO_FILE_TYPE, MigratedForms } from "../../utils";

export function getFieldCreationType(
  inferredValue: unknown,
  value: unknown,
  sourceField: SourceFields | undefined,
): FieldCreationType {
  if (inferredValue != null && value != null) {
    return FIELD_CREATION_TYPE.INFERENCE;
  }
  if (sourceField?.creation_type) {
    return sourceField.creation_type;
  }
  return FIELD_CREATION_TYPE.USER;
}

export function getManuallyEdited(
  inferredValue: unknown,
  value: unknown,
  previousFieldValue: unknown,
  sourceField: SourceFields | undefined,
): boolean {
  if (inferredValue != null) {
    return value !== inferredValue;
  }
  if (previousFieldValue != null) {
    return previousFieldValue !== value || !!sourceField?.manually_edited;
  }
  return false;
}

export const useGetDischargeLetterOnSubmit = <T extends DocsFormName>({
  auction,
  formData: previousFormData,
}: {
  auction: Auction;
  formData: DocsFormDataMapped<T> | null | undefined;
}) => {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [generateForm, generateFormProgress, resetProgress] = useSearchAction({
    actionType: SEARCH_ACTION_GENERATE_FORM,
    refetchQueries: [
      {
        context: { encryptionContext },
        query: GET_AUCTION_FORMS,
        variables: { auctionId: auction.id },
      },
    ],
  });

  const onSubmit = useCallback(
    async (
      formData: DocsFormDataMapped<typeof formName>,
      formName: MigratedForms,
      sourceInformation: SourceInformation<typeof formName>,
      docsFillFormResult: DocsFormStatusResponse | null,
    ) => {
      const sourceFields: FormMetadata<typeof formName>["source_fields"] =
        Object.entries(formData).reduce(
          (acc, [key, value]) => {
            const inferredValue = docsFillFormResult?.form[key]?.value;
            const sourceField = sourceInformation.getSourceFields(
              key as keyof DocsFormDataMapped<typeof formName>,
            );
            const sourceDocuments = sourceField?.source_documents ?? [];
            const previousFieldValue =
              previousFormData?.[key as keyof DocsFormDataMapped<T>];

            return {
              ...acc,
              [key]: {
                creation_type: getFieldCreationType(
                  inferredValue,
                  value,
                  sourceField,
                ),
                manually_edited: getManuallyEdited(
                  inferredValue,
                  value,
                  previousFieldValue,
                  sourceField,
                ),
                source_documents: sourceDocuments,
              },
            };
          },
          {} as FormMetadata<typeof formName>["source_fields"],
        );

      const submittedFormData: DocsSubmittedFormData<typeof formName> = {
        values: formData,
      };

      const metadata: FormMetadata<typeof formName> = {
        source_fields: sourceFields,
      };

      const updatedAuction = {
        ...auction,
        forms: [
          {
            form_data: convertEncryptedFieldOut(
              JSON.stringify(submittedFormData),
            ),
            metadata,
          },
        ],
      } as Auction;

      await generateForm({
        auction,
        context: {
          auction: updatedAuction,
          form_type: FORM_NAME_TO_FILE_TYPE[formName],
        },
        onCompleted: () => {
          resetProgress();
        },
        onError: (err) => {
          console.error(`error saving form - ${getErrorMessage(err)}`);
        },
      });
    },
    [
      convertEncryptedFieldOut,
      generateForm,
      auction,
      previousFormData,
      encryptionContext,
    ],
  );

  return { onSubmit, onSubmitProgress: generateFormProgress };
};
